import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * SPV PLP component.
 * Manages the bottom-sheet (mobile) / right-side (desktop) filter drawer that
 * sits over the auto-rendered pill row + Shopify-native results grid.
 *
 * Filter state itself is owned by the existing <facets-form-component> nested
 * inside the drawer — this component only handles drawer chrome:
 * open / close / focus-trap / scroll-lock / ESC / Reset / drag-to-dismiss.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} drawer
 * @property {HTMLElement} scrim
 * @property {HTMLButtonElement} dragHandle
 * @property {HTMLElement} [resetCount]
 * @property {HTMLFormElement} [drawerForm]
 *
 * @extends {Component<Refs>}
 */
class SpvPlp extends Component {
  requiredRefs = ['drawer', 'scrim'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('input', this.#onPriceSliderInput);
    this.#syncPillActiveState();
    this.#bindDragHandle();
  }

  updatedCallback() {
    super.updatedCallback?.();
    this.#syncPillActiveState();
  }

  /**
   * Highlights the active pill based on the URL's current filter param.
   * Needed when `collection.filters` is unavailable (the merchant hasn't
   * enabled this filter in admin) — Liquid can't read the query string,
   * so JS picks up the slack after each render.
   */
  #syncPillActiveState() {
    const pillRow = this.querySelector('.spv-plp__pills');
    if (!pillRow) return;
    const param = pillRow.dataset.pillParam;
    if (!param) return;
    const url = new URL(window.location.href);
    const current = url.searchParams.get(param) || '';
    pillRow.querySelectorAll('label.spv-plp-pill').forEach((label) => {
      const input = label.querySelector('input');
      if (!(input instanceof HTMLInputElement)) return;
      const matches = input.value === current;
      input.checked = matches;
      label.classList.toggle('is-active', matches);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('input', this.#onPriceSliderInput);
    this.#unbindDragHandle();
  }

  openDrawer(_, event) {
    const { drawer, scrim } = this.refs;
    drawer.setAttribute('aria-hidden', 'false');
    scrim.setAttribute('data-visible', 'true');
    document.documentElement.classList.add('spv-scroll-lock');

    const trigger = event?.target instanceof Element ? event.target.closest('[aria-expanded]') : null;
    trigger?.setAttribute('aria-expanded', 'true');
    this.#lastTrigger = trigger;

    onAnimationEnd(drawer, () => trapFocus(drawer), { subtree: false });
  }

  closeDrawer() {
    const { drawer, scrim } = this.refs;
    drawer.setAttribute('aria-hidden', 'true');
    scrim.setAttribute('data-visible', 'false');
    document.documentElement.classList.remove('spv-scroll-lock');
    removeTrapFocus();

    if (this.#lastTrigger) {
      this.#lastTrigger.setAttribute('aria-expanded', 'false');
      this.#lastTrigger.focus();
      this.#lastTrigger = null;
    }
  }

  /**
   * Forwards a change event from the drawer (which lives outside the
   * <facets-form-component>) to the facets form so it builds URL params
   * + re-renders the section. Inputs in the drawer use form="..." to
   * associate with the form, so FormData() picks them up.
   *
   * The drawer wrapper has `data-skip-node-update` so the morph won't reset
   * `aria-hidden` (which would snap it shut). `<details>` open state is
   * preserved by morph.js's default behavior. Scroll position is captured
   * here and restored on the next two animation frames — the second rAF
   * lands after the morph has committed to the DOM.
   */
  notifyFilterChange() {
    const content = this.refs.drawer?.querySelector('.spv-plp-drawer__content');
    const savedScroll = content?.scrollTop ?? 0;

    const facets = this.querySelector('facets-form-component');
    if (facets && typeof facets.updateFilters === 'function') {
      facets.updateFilters();
    }

    if (savedScroll > 0) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const newContent = this.refs.drawer?.querySelector('.spv-plp-drawer__content');
          if (newContent) newContent.scrollTop = savedScroll;
        });
      });
    }
  }

  /**
   * "Ver resultados" footer button handler. Filter changes already auto-applied
   * on each input change, so this is now just a close — no extra render needed.
   */
  applyAndClose() {
    this.closeDrawer();
  }

  /**
   * Clears every filter input inside the drawer EXCEPT the pill dimension
   * (which is rendered outside the drawer in spv-plp-pill-row).
   *
   * The drawer has no <form> of its own — its inputs are associated with
   * the outside form via `form="..."` attribute (see spv-plp.liquid), so we
   * query inputs structurally inside the drawer here. After clearing we go
   * through `notifyFilterChange()` — same path used by every drawer input's
   * change event — to drive the AJAX section re-render via
   * `facets-form-component.updateFilters()`.
   */
  resetDrawerFilters() {
    const { drawer } = this.refs;
    if (!drawer) return;
    const pillParam = this.dataset.pillParam || '';
    const pillValuePrefix = this.dataset.pillValuePrefix || '';

    let mutated = false;
    drawer.querySelectorAll('input[name^="filter."]').forEach((el) => {
      if (!(el instanceof HTMLInputElement)) return;
      if (pillParam && el.name === pillParam) {
        // No prefix → pill row owns the entire dimension, preserve every input.
        // With a prefix → only the pill row's slice (empty "All" value or
        // values starting with the prefix) is preserved; other inputs sharing
        // the same name (e.g. brand:*, season:* checkboxes from drawer-tag-group
        // blocks) belong to the drawer and must be cleared.
        if (!pillValuePrefix) return;
        if (el.value === '' || el.value.startsWith(pillValuePrefix)) return;
      }
      if (el.type === 'checkbox' || el.type === 'radio') {
        if (el.checked) {
          el.checked = false;
          mutated = true;
        }
      } else if (el.value !== '') {
        el.value = '';
        mutated = true;
      }
    });

    if (mutated) this.notifyFilterChange();
  }

  /**
   * "All" pill (pill_grid drawer style): unchecks every selected value in its
   * own grid, then re-applies filters via the standard path. Scoped to the
   * clicked grid so prefix-grouped tag facets only clear their own slice.
   * @param {Event} event
   */
  clearPillGrid(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const grid = target.closest('.spv-plp-pill-grid');
    if (!grid) return;

    let mutated = false;
    grid.querySelectorAll('input[type="checkbox"]').forEach((el) => {
      if (el instanceof HTMLInputElement && el.checked) {
        el.checked = false;
        mutated = true;
      }
    });

    if (mutated) this.notifyFilterChange();
  }

  /** @type {Element|null} */
  #lastTrigger = null;

  /** @param {KeyboardEvent} event */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;
    if (this.refs.drawer.getAttribute('aria-hidden') === 'false') {
      this.closeDrawer();
    }
  };

  // ─── Drag-to-dismiss (mobile) ────────────────────────────────────────────
  // The handle button at the top of the bottom-sheet drawer accepts a
  // touch-drag gesture. On release, the drawer closes if the user dragged
  // past 30% of its height OR released with sufficient downward velocity.
  // Mouse and keyboard fall through to the button's `on:click="/closeDrawer"`.

  /** @type {number|null} */
  #dragStartY = null;
  /** @type {number} */
  #dragStartTime = 0;
  /** @type {number} */
  #dragDelta = 0;
  /** @type {number|null} */
  #dragPointerId = null;

  #bindDragHandle() {
    const handle = this.refs.dragHandle;
    if (!(handle instanceof HTMLElement)) return;
    handle.addEventListener('pointerdown', this.#onDragPointerDown);
    handle.addEventListener('pointermove', this.#onDragPointerMove);
    handle.addEventListener('pointerup', this.#onDragPointerEnd);
    handle.addEventListener('pointercancel', this.#onDragPointerEnd);
  }

  #unbindDragHandle() {
    const handle = this.refs.dragHandle;
    if (!(handle instanceof HTMLElement)) return;
    handle.removeEventListener('pointerdown', this.#onDragPointerDown);
    handle.removeEventListener('pointermove', this.#onDragPointerMove);
    handle.removeEventListener('pointerup', this.#onDragPointerEnd);
    handle.removeEventListener('pointercancel', this.#onDragPointerEnd);
  }

  /** @param {PointerEvent} event */
  #onDragPointerDown = (event) => {
    // Only touch / pen — let mouse fall through to the click handler that closes via /closeDrawer.
    if (event.pointerType !== 'touch' && event.pointerType !== 'pen') return;
    const handle = event.currentTarget;
    if (!(handle instanceof HTMLElement)) return;
    handle.setPointerCapture(event.pointerId);
    this.#dragPointerId = event.pointerId;
    this.#dragStartY = event.clientY;
    this.#dragStartTime = performance.now();
    this.#dragDelta = 0;
    // Disable the CSS slide-up easing so the drawer follows the finger 1:1.
    this.refs.drawer.style.transition = 'none';
  };

  /** @param {PointerEvent} event */
  #onDragPointerMove = (event) => {
    if (this.#dragStartY === null || event.pointerId !== this.#dragPointerId) return;
    const delta = Math.max(0, event.clientY - this.#dragStartY);
    this.#dragDelta = delta;
    this.refs.drawer.style.transform = `translateY(${delta}px)`;
    // Fade the scrim proportionally to the drag distance.
    const fade = Math.max(0, 1 - delta / Math.max(window.innerHeight, 1));
    this.refs.scrim.style.opacity = String(fade);
  };

  // ─── Price range slider ──────────────────────────────────────────────────
  // Two overlapping `<input type="range">` elements share one track. On
  // `input` (drag, keyboard, etc.) we clamp so min ≤ max, paint the colored
  // fill via CSS variables, and update the formatted price readouts. The
  // submit goes through the standard panel `on:change="/notifyFilterChange"`
  // listener — no extra plumbing needed here.

  /** @param {Event} event */
  #onPriceSliderInput = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.type !== 'range') return;
    const slider = target.closest('[data-spv-price-slider]');
    if (!(slider instanceof HTMLElement)) return;

    const minInput = slider.querySelector('[data-spv-price-min]');
    const maxInput = slider.querySelector('[data-spv-price-max]');
    if (!(minInput instanceof HTMLInputElement) || !(maxInput instanceof HTMLInputElement)) return;

    // Clamp so min ≤ max — push the *other* handle if the user drags one past it.
    const minVal = Number(minInput.value);
    const maxVal = Number(maxInput.value);
    if (minVal > maxVal) {
      if (target === minInput) minInput.value = String(maxVal);
      else maxInput.value = String(minVal);
    }

    this.#paintPriceSlider(slider, minInput, maxInput);
    this.#updatePriceReadouts(slider, minInput, maxInput);
    this.#syncPriceHiddenInputs(slider, minInput, maxInput);
  };

  /**
   * Mirrors the slider handles into the hidden form inputs, but only when a
   * handle sits *off* its default edge. A min at the floor (0) or a max at the
   * ceiling (range max) writes an empty value, which createURLParameters then
   * strips — so an untouched slider never contributes a price param.
   * @param {HTMLElement} slider
   * @param {HTMLInputElement} minInput
   * @param {HTMLInputElement} maxInput
   */
  #syncPriceHiddenInputs(slider, minInput, maxInput) {
    const minHidden = slider.querySelector('[data-spv-price-min-value]');
    const maxHidden = slider.querySelector('[data-spv-price-max-value]');
    if (minHidden instanceof HTMLInputElement) {
      minHidden.value = Number(minInput.value) > Number(minInput.min) ? minInput.value : '';
    }
    if (maxHidden instanceof HTMLInputElement) {
      maxHidden.value = Number(maxInput.value) < Number(maxInput.max) ? maxInput.value : '';
    }
  }

  /**
   * @param {HTMLElement} slider
   * @param {HTMLInputElement} minInput
   * @param {HTMLInputElement} maxInput
   */
  #paintPriceSlider(slider, minInput, maxInput) {
    const range = Number(minInput.max);
    if (!Number.isFinite(range) || range <= 0) return;
    const minPct = (Number(minInput.value) / range) * 100;
    const maxPct = (Number(maxInput.value) / range) * 100;
    slider.style.setProperty('--spv-price-min', `${minPct}%`);
    slider.style.setProperty('--spv-price-max', `${maxPct}%`);
  }

  /**
   * @param {HTMLElement} slider
   * @param {HTMLInputElement} minInput
   * @param {HTMLInputElement} maxInput
   */
  #updatePriceReadouts(slider, minInput, maxInput) {
    const minOut = slider.querySelector('[data-spv-price-min-readout]');
    const maxOut = slider.querySelector('[data-spv-price-max-readout]');
    if (!minOut && !maxOut) return;

    const currency = slider.dataset.currency || 'USD';
    const locale = slider.dataset.locale || (typeof navigator !== 'undefined' ? navigator.language : 'en-US');
    let formatter;
    try {
      // Default fraction digits per currency rules (EUR/USD 2, JPY 0, etc.).
      formatter = new Intl.NumberFormat(locale, { style: 'currency', currency });
    } catch {
      formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
    }
    if (minOut) minOut.textContent = formatter.format(Number(minInput.value));
    if (maxOut) maxOut.textContent = formatter.format(Number(maxInput.value));
  }

  /** @param {PointerEvent} event */
  #onDragPointerEnd = (event) => {
    if (this.#dragStartY === null || event.pointerId !== this.#dragPointerId) return;

    const delta = this.#dragDelta;
    const elapsed = performance.now() - this.#dragStartTime;
    const velocity = elapsed > 0 ? delta / elapsed : 0; // px per ms

    // Reset drag state + restore CSS-driven transition for the snap animation.
    this.#dragStartY = null;
    this.#dragPointerId = null;
    this.refs.drawer.style.transition = '';
    this.refs.drawer.style.transform = '';
    this.refs.scrim.style.opacity = '';

    const drawerHeight = this.refs.drawer.getBoundingClientRect().height;
    const distanceCloses = drawerHeight > 0 && delta > drawerHeight * 0.3;
    const velocityCloses = velocity > 0.6; // ~600 px/sec — comfortable flick

    if (distanceCloses || velocityCloses) {
      this.closeDrawer();
    }
  };
}

if (!customElements.get('spv-plp-component')) {
  customElements.define('spv-plp-component', SpvPlp);
}
